<form [formGroup]="loanProductSettingsForm">
  <div class="layout-row-wrap gap-2percent responsive-column align-start-center">
    <mat-form-field class="flex-30">
      <mat-label>{{ 'labels.inputs.products.loan.Amortization' | translate }}</mat-label>
      <mat-select
        formControlName="amortizationType"
        matTooltip="{{ 'tooltips.The Amortization value' | translate }}"
        required
      >
        @for (amortizationType of amortizationTypeData; track amortizationType) {
          <mat-option [value]="amortizationType.id">
            {{ 'labels.inputs.products.loan.amortizationType.' + amortizationType.value | translate }}
          </mat-option>
        }
      </mat-select>
      <mat-error>
        {{ 'labels.inputs.products.loan.Amortization' | translate }} {{ 'labels.commons.is' | translate }}
        <strong>{{ 'labels.commons.required' | translate }}</strong>
      </mat-error>
    </mat-form-field>

    <mat-form-field class="flex-30">
      <mat-label>{{ 'labels.inputs.Interest Method' | translate }}</mat-label>
      <mat-select
        formControlName="interestType"
        matTooltip="{{ 'tooltips.The Interest method value' | translate }}"
        required
      >
        @for (interestType of interestTypeData; track interestType) {
          <mat-option [value]="interestType.id">
            {{ interestType.value | translateKey: 'catalogs' }}
          </mat-option>
        }
      </mat-select>
      <mat-error>
        {{ 'labels.inputs.Interest Method' | translate }} {{ 'labels.commons.is' | translate }}
        <strong>{{ 'labels.commons.required' | translate }}</strong>
      </mat-error>
    </mat-form-field>

    <mat-form-field class="flex-30">
      <mat-label>{{ 'labels.inputs.Interest calculation period' | translate }}</mat-label>
      <mat-select
        formControlName="interestCalculationPeriodType"
        matTooltip="{{ 'tooltips.Daily - Will Calculate the interest' | translate }}"
        required
      >
        @for (interestCalculationPeriodType of interestCalculationPeriodTypeData; track interestCalculationPeriodType) {
          <mat-option [value]="interestCalculationPeriodType.id">
            {{ interestCalculationPeriodType.value | translateKey: 'catalogs' }}
          </mat-option>
        }
      </mat-select>
      <mat-error>
        {{ 'labels.inputs.Interest calculation period' | translate }} {{ 'labels.commons.is' | translate }}
        <strong>{{ 'labels.commons.required' | translate }}</strong>
      </mat-error>
    </mat-form-field>

    <mat-checkbox class="flex-96" labelPosition="before" formControlName="isEqualAmortization">
      {{ 'labels.inputs.products.loan.Is Equal Amortization?' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.interestCalculationPeriodType === 1) {
      <mat-checkbox
        class="flex-96"
        labelPosition="before"
        matTooltip="{{ 'tooltips.To be used with SAME AS REPAYMENT PERIOD' | translate }}"
        formControlName="allowPartialPeriodInterestCalculation"
      >
        {{ 'labels.inputs.Calculate interest for exact days in partial period' | translate }}
      </mat-checkbox>
    }

    <mat-divider class="flex-98"></mat-divider>

    <h4 class="mat-h4 flex-98">{{ 'labels.inputs.Loan Schedule' | translate }}</h4>

    <mat-form-field class="flex-30">
      <mat-label>{{ 'labels.inputs.Loan Schedule Type' | translate }}</mat-label>
      <mat-select formControlName="loanScheduleType" required>
        @for (loanScheduleType of loanScheduleTypeData; track loanScheduleType) {
          <mat-option [value]="loanScheduleType.code">
            {{ loanScheduleType.value | translateKey: 'catalogs' }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field class="flex-30">
      <mat-label>{{ 'labels.inputs.Repayment strategy' | translate }}</mat-label>
      <mat-select
        formControlName="transactionProcessingStrategyCode"
        matTooltip="{{ 'tooltips.The repayment strategy' | translate }}"
        required
        [disabled]="advancedTransactionProcessingStrategyDisabled"
      >
        @for (transactionProcessingStrategy of transactionProcessingStrategyData; track transactionProcessingStrategy) {
          <mat-option [value]="transactionProcessingStrategy.code">
            {{ transactionProcessingStrategy.name | translateKey: 'catalogs' }}
          </mat-option>
        }
      </mat-select>
      <mat-error>
        {{ 'labels.inputs.Repayment strategy' | translate }} {{ 'labels.commons.is' | translate }}
        <strong>{{ 'labels.commons.required' | translate }}</strong>
      </mat-error>
    </mat-form-field>

    @if (isAdvancedTransactionProcessingStrategy) {
      <mat-form-field class="flex-fill flex-30">
        <mat-label>{{ 'labels.inputs.Loan Schedule Processing Type' | translate }}</mat-label>
        <mat-select formControlName="loanScheduleProcessingType" required>
          @for (loanScheduleProcessingType of loanScheduleProcessingTypeData; track loanScheduleProcessingType) {
            <mat-option [value]="loanScheduleProcessingType.code">
              {{ loanScheduleProcessingType.value | translateKey: 'catalogs' }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    <h3 class="mat-h3 flex-96">{{ 'labels.inputs.Loan Tranche Details' | translate }}</h3>

    <mat-checkbox
      labelPosition="before"
      formControlName="multiDisburseLoan"
      matTooltip="{{ 'tooltips.Leave this checkbox unchecked' | translate }}"
      class="margin-b flex-73"
    >
      {{ 'labels.inputs.Enable Multiple Disbursals' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.multiDisburseLoan) {
      <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Maximum Tranche count' | translate }}</mat-label>
          <input
            type="number"
            matInput
            matTooltip="{{ 'tooltips.Maximum number of disbursements' | translate }}"
            formControlName="maxTrancheCount"
            required
          />
          @if (loanProductSettingsForm.controls.maxTrancheCount.hasError('required')) {
            <mat-error>
              {{ 'labels.inputs.Maximum Tranche count' | translate }} {{ 'labels.commons.is' | translate }}
              <strong>{{ 'labels.commons.required' | translate }}</strong>
            </mat-error>
          }
        </mat-form-field>
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Maximum allowed outstanding balance' | translate }}</mat-label>
          <input
            type="number"
            matInput
            matTooltip="{{ 'tooltips.Maximum outstanding loan account balance' | translate }}"
            formControlName="outstandingLoanBalance"
          />
        </mat-form-field>
        <mat-checkbox class="flex-48 margin-v" labelPosition="before" formControlName="disallowExpectedDisbursements">
          {{ 'labels.inputs.Disallow Expected Disbursements' | translate }}
        </mat-checkbox>
        @if (loanProductSettingsForm.value.loanScheduleType === 'PROGRESSIVE') {
          <mat-checkbox
            class="flex-48 margin-v"
            labelPosition="before"
            formControlName="allowFullTermForTranche"
            matTooltip="{{ 'tooltips.Allow full term length for each tranche disbursement' | translate }}"
          >
            {{ 'labels.inputs.Allow full term for each tranche' | translate }}
          </mat-checkbox>
        }
      </div>
    }

    <h3 class="mat-h3 flex-96">{{ 'labels.heading.Down Payment' | translate }}</h3>

    <mat-checkbox
      labelPosition="before"
      formControlName="enableDownPayment"
      matTooltip="{{ 'tooltips.Leave this checkbox unchecked' | translate }}"
      class="margin-b flex-73"
    >
      {{ 'labels.inputs.Enable Down Payment' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.enableDownPayment) {
      <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Disbursed Amount Percentage Down Payment' | translate }} (%)</mat-label>
          <input
            type="number"
            matInput
            matTooltip="{{ 'tooltips.The percentage of disbursements amount for Down Payment' | translate }}"
            formControlName="disbursedAmountPercentageForDownPayment"
            required
            [min]="0"
            [max]="100"
          />
          @if (loanProductSettingsForm.controls.disbursedAmountPercentageForDownPayment.hasError('required')) {
            <mat-error>
              {{ 'labels.inputs.Disbursed Amount Percentage Down Payment' | translate }}
              {{ 'labels.commons.is' | translate }} <strong>{{ 'labels.commons.required' | translate }}</strong>
            </mat-error>
          }
          @if (loanProductSettingsForm.controls.disbursedAmountPercentageForDownPayment.errors?.range) {
            <mat-error>
              {{ 'labels.inputs.Disbursed Amount Percentage Down Payment' | translate }}
              {{ 'labels.commons.is' | translate }} {{ 'labels.text.out' | translate }}
              <strong>{{ 'labels.text.percentage range' | translate }}</strong>
            </mat-error>
          }
        </mat-form-field>
        <mat-checkbox
          class="flex-48 margin-v"
          labelPosition="before"
          formControlName="enableAutoRepaymentForDownPayment"
          matTooltip="{{ 'tooltips.Enable or disable the auto repayment of down-payment.' | translate }}"
        >
          {{ 'labels.inputs.Enable Auto Repayment for Down Payment' | translate }}
        </mat-checkbox>
      </div>
    }

    <mat-divider class="flex-98"></mat-divider>

    <span class="flex-48 hide-lt-md"></span>

    <div class="full-width-flex">
      @if (isAdvancedTransactionProcessingStrategy) {
        <div>
          <h3 class="mat-h4 flex-98">{{ 'labels.inputs.Loan Charge-off behaviour' | translate }}</h3>
          <mat-form-field class="flex-30">
            <mat-label>{{ 'labels.inputs.Loan Charge-off behaviour' | translate }}</mat-label>
            <mat-select formControlName="chargeOffBehaviour" required>
              @for (chargeOffBehaviour of chargeOffBehaviourData; track chargeOffBehaviour) {
                <mat-option [value]="chargeOffBehaviour.id">
                  {{ chargeOffBehaviour.value | translateKey: 'catalogs' }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      }
    </div>

    <h4 class="mat-h4 m-r-10 flex-98">
      {{ 'labels.inputs.Moratorium' | translate }}
      <i class="fas fa-question" matTooltip="{{ 'tooltips.The moratorium information' | translate }}"></i>
    </h4>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Grace on principal payment' | translate }}</mat-label>
      <input type="number" matInput formControlName="graceOnPrincipalPayment" />
    </mat-form-field>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Grace on interest payment' | translate }}</mat-label>
      <input type="number" matInput formControlName="graceOnInterestPayment" />
    </mat-form-field>

    <mat-divider class="flex-98"></mat-divider>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Delinquency Bucket' | translate }}</mat-label>
      <mat-select formControlName="delinquencyBucketId">
        @for (delinquencyBucket of delinquencyBucketData; track delinquencyBucket) {
          <mat-option [value]="delinquencyBucket.id">
            {{ delinquencyBucket.name }}
          </mat-option>
        }
      </mat-select>
      @if (loanProductSettingsForm.controls.delinquencyBucketId) {
        <button matSuffix mat-icon-button aria-label="Clear" (click)="clearProperty($event, 'delinquencyBucketId')">
          <fa-icon icon="close" size="md"></fa-icon>
        </button>
      }
    </mat-form-field>

    @if (loanProductSettingsForm.value.delinquencyBucketId) {
      <mat-checkbox class="flex-48 margin-v" labelPosition="before" formControlName="enableInstallmentLevelDelinquency">
        {{ 'labels.inputs.Enable installment level Delinquency' | translate }}
      </mat-checkbox>
    }

    <mat-divider class="flex-98"></mat-divider>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Interest free period' | translate }}</mat-label>
      <input
        type="number"
        matInput
        matTooltip="{{ 'tooltips.If the Interest Free Period' | translate }}"
        formControlName="graceOnInterestCharged"
      />
    </mat-form-field>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Arrears tolerance' | translate }}</mat-label>
      <input
        type="number"
        matInput
        matTooltip="{{ 'tooltips.With Arrears tolerance' | translate }}"
        formControlName="inArrearsTolerance"
      />
    </mat-form-field>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Days in year' | translate }}</mat-label>
      <mat-select
        matTooltip="{{ 'tooltips.To calculate interest' | translate }}"
        formControlName="daysInYearType"
        required
      >
        @for (daysInYearType of daysInYearTypeData; track daysInYearType) {
          <mat-option [value]="daysInYearType.id">
            {{ daysInYearType.value | translateKey: 'catalogs' }}
          </mat-option>
        }
      </mat-select>
      <mat-error>
        {{ 'labels.inputs.Days in year' | translate }} {{ 'labels.commons.is' | translate }}
        <strong>{{ 'labels.commons.required' | translate }}</strong>
      </mat-error>
    </mat-form-field>

    @if (useDaysInYearCustomStrategy) {
      <mat-form-field class="flex-48">
        <mat-label>{{ 'labels.inputs.Days in year custom strategy' | translate }}</mat-label>
        <mat-select
          matTooltip="{{ 'tooltips.To calculate interest' | translate }}"
          formControlName="daysInYearCustomStrategy"
        >
          @for (daysInYearCustomStrategy of daysInYearCustomStrategyOptions; track daysInYearCustomStrategy) {
            <mat-option [value]="daysInYearCustomStrategy.id">
              {{ daysInYearCustomStrategy.value | translateKey: 'catalogs' }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Days in month' | translate }}</mat-label>
      <mat-select
        matTooltip="{{ 'tooltips.Number of days in month' | translate }}."
        formControlName="daysInMonthType"
        required
      >
        @for (daysInMonthType of daysInMonthTypeData; track daysInMonthType) {
          <mat-option [value]="daysInMonthType.id">
            {{ daysInMonthType.value | translateKey: 'catalogs' }}
          </mat-option>
        }
      </mat-select>
      <mat-error>
        {{ 'labels.inputs.Days in month' | translate }} {{ 'labels.commons.is' | translate }}
        <strong>{{ 'labels.commons.required' | translate }}</strong>
      </mat-error>
    </mat-form-field>

    <mat-checkbox labelPosition="before" formControlName="canDefineInstallmentAmount" class="margin-v flex-98">
      {{ 'labels.inputs.Allow fixing of the installment amount' | translate }}
    </mat-checkbox>

    <h4 class="flex-48">
      {{ 'labels.inputs.Number of days a loan may be overdue before moving into arrears' | translate }}
      <i
        class="fas fa-question"
        matTooltip="{{ 'tooltips.A loan is in arrears once the number of days' | translate }}"
      ></i>
    </h4>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.On arrears ageing' | translate }}</mat-label>
      <input type="number" matInput formControlName="graceOnArrearsAgeing" />
    </mat-form-field>

    <h4 class="flex-48">
      {{
        'labels.inputs.Maximum number of days a loan may be overdue before becoming a NPA (non performing asset)'
          | translate
      }}
      <i
        class="fas fa-question"
        matTooltip="{{ 'tooltips.NPA (non performing asset) once the number of days' | translate }}"
      ></i>
    </h4>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Overdue days for NPA' | translate }}</mat-label>
      <input type="number" matInput formControlName="overdueDaysForNPA" />
    </mat-form-field>

    <mat-checkbox
      class="flex-48 margin-v"
      labelPosition="before"
      formControlName="accountMovesOutOfNPAOnlyOnArrearsCompletion"
    >
      {{ 'labels.inputs.Account moves out of NPA only after all arrears have been cleared' | translate }}
    </mat-checkbox>

    <mat-form-field class="flex-48">
      <mat-label>{{ 'labels.inputs.Principal Threshold (%) for Last Instalment' | translate }}</mat-label>
      <input type="number" matInput formControlName="principalThresholdForLastInstallment" />
    </mat-form-field>

    <mat-checkbox
      class="flex-48 margin-v"
      labelPosition="before"
      matTooltip="{{ 'tooltips.Fields are used to define the minimum, maximum gap' | translate }}"
      formControlName="allowVariableInstallments"
    >
      {{ 'labels.inputs.Are Variable Installments allowed?' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.allowVariableInstallments) {
      <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <h4 class="mat-h4 flex-98">{{ 'labels.inputs.Variable Installments' | translate }}</h4>
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Minimum gap between Installments' | translate }}</mat-label>
          <input type="number" matInput formControlName="minimumGap" required />
          <mat-error>
            {{ 'labels.inputs.Minimum gap between Installments' | translate }} {{ 'labels.commons.is' | translate }}
            <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Maximum gap between Installments' | translate }}</mat-label>
          <input type="number" matInput formControlName="maximumGap" required />
          <mat-error>
            {{ 'labels.inputs.Maximum gap between Installments' | translate }} {{ 'labels.commons.is' | translate }}
            <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
      </div>
    }

    <mat-checkbox
      class="flex-48 margin-v"
      labelPosition="before"
      matTooltip="{{ 'tooltips.Loan Product can be used to apply for Topup Loans' | translate }}"
      formControlName="canUseForTopup"
    >
      {{ 'labels.inputs.Allowed to be used for providing Topup Loans' | translate }}
    </mat-checkbox>

    <mat-divider class="flex-98"></mat-divider>

    <h3 class="mat-h3 flex-96">{{ 'labels.heading.Interest Recalculation' | translate }}</h3>

    <mat-checkbox class="flex-73 margin-b" labelPosition="before" formControlName="isInterestRecalculationEnabled">
      {{ 'labels.inputs.Recalculate Interest' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.isInterestRecalculationEnabled) {
      <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Pre-closure interest calculation rule' | translate }}</mat-label>
          <mat-select formControlName="preClosureInterestCalculationStrategy" required>
            @for (
              preClosureInterestCalculationStrategy of preClosureInterestCalculationStrategyData;
              track preClosureInterestCalculationStrategy
            ) {
              <mat-option [value]="preClosureInterestCalculationStrategy.id">
                {{ preClosureInterestCalculationStrategy.value | translateKey: 'catalogs' }}
              </mat-option>
            }
          </mat-select>
          <mat-error>
            {{ 'labels.inputs.Pre-closure interest calculation rule' | translate }}
            {{ 'labels.commons.is' | translate }}
            <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Advance payments adjustment type' | translate }}</mat-label>
          <mat-select formControlName="rescheduleStrategyMethod" required [disabled]="rescheduleStrategyTypeDisabled">
            @for (rescheduleStrategyType of rescheduleStrategyTypeData; track rescheduleStrategyType) {
              <mat-option [value]="rescheduleStrategyType.id">
                {{ rescheduleStrategyType.value | translateKey: 'catalogs' }}
              </mat-option>
            }
          </mat-select>
          <mat-error>
            {{ 'labels.inputs.Advance payments adjustment type' | translate }} {{ 'labels.commons.is' | translate }}
            <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Interest recalculation compounding on' | translate }}</mat-label>
          <mat-select formControlName="interestRecalculationCompoundingMethod" required>
            @for (
              interestRecalculationCompoundingType of interestRecalculationCompoundingTypeData;
              track interestRecalculationCompoundingType
            ) {
              <mat-option [value]="interestRecalculationCompoundingType.id">
                {{ interestRecalculationCompoundingType.value | translateKey: 'catalogs' }}
              </mat-option>
            }
          </mat-select>
          <mat-error>
            {{ 'labels.inputs.Interest recalculation compounding on' | translate }}
            {{ 'labels.commons.is' | translate }}
            <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
        <span class="flex-48 hide-lt-md"></span>
        <div class="flex-fill layout-row-wrap gap-2percent layout-lt-md-column">
          @if (
            loanProductSettingsForm.value.interestRecalculationCompoundingMethod !== 0 &&
            loanProductSettingsForm.value.recalculationCompoundingFrequencyType !== 1
          ) {
            <mat-form-field class="flex-48">
              <mat-label>{{ 'labels.inputs.Frequency Interval for compounding' | translate }}</mat-label>
              <input type="number" matInput formControlName="recalculationCompoundingFrequencyInterval" required />
              <mat-error>
                {{ 'labels.inputs.Frequency Interval for compounding' | translate }}
                {{ 'labels.commons.is' | translate }}
                <strong>{{ 'labels.commons.required' | translate }}</strong>
              </mat-error>
            </mat-form-field>
          }
          @if (loanProductSettingsForm.value.recalculationRestFrequencyType !== 1) {
            <mat-form-field class="flex-48">
              <mat-label>{{ 'labels.inputs.Frequency Interval for recalculation' | translate }}</mat-label>
              <input type="number" matInput formControlName="recalculationRestFrequencyInterval" required />
              <mat-error>
                {{ 'labels.inputs.Frequency Interval for recalculation' | translate }}
                {{ 'labels.commons.is' | translate }} <strong>{{ 'labels.commons.required' | translate }}</strong>
              </mat-error>
            </mat-form-field>
          }
        </div>
        @if (loanProductSettingsForm.value.interestRecalculationCompoundingMethod !== 0) {
          <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
            <mat-form-field class="flex-31">
              <mat-label>{{ 'labels.inputs.Frequency for compounding' | translate }}</mat-label>
              <mat-select formControlName="recalculationCompoundingFrequencyType" required>
                @for (
                  interestRecalculationFrequencyType of interestRecalculationFrequencyTypeData;
                  track interestRecalculationFrequencyType
                ) {
                  <mat-option [value]="interestRecalculationFrequencyType.id">
                    {{ interestRecalculationFrequencyType.value }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (loanProductSettingsForm.value.recalculationCompoundingFrequencyType === 4) {
              <div class="flex-31 layout-row layout-lt-md-column">
                <mat-form-field class="flex-fill">
                  <mat-select formControlName="recalculationCompoundingFrequencyNthDayType">
                    @for (
                      interestRecalculationNthDayType of interestRecalculationNthDayTypeData;
                      track interestRecalculationNthDayType
                    ) {
                      <mat-option [value]="interestRecalculationNthDayType.id">
                        {{ interestRecalculationNthDayType.value | translateKey: 'catalogs' }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            }
            @if (
              (loanProductSettingsForm.value.recalculationCompoundingFrequencyType === 4 &&
                loanProductSettingsForm.value.recalculationCompoundingFrequencyNthDayType !== -2) ||
              loanProductSettingsForm.value.recalculationCompoundingFrequencyType === 3
            ) {
              <div class="flex-31 layout-row layout-lt-md-column">
                <mat-form-field class="flex-fill">
                  <mat-select formControlName="recalculationCompoundingFrequencyDayOfWeekType">
                    @for (
                      interestRecalculationDayOfWeekType of interestRecalculationDayOfWeekTypeData;
                      track interestRecalculationDayOfWeekType
                    ) {
                      <mat-option [value]="interestRecalculationDayOfWeekType.id">
                        {{ interestRecalculationDayOfWeekType.value | translateKey: 'catalogs' }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            }
            @if (
              loanProductSettingsForm.value.recalculationCompoundingFrequencyType === 4 &&
              loanProductSettingsForm.value.recalculationCompoundingFrequencyNthDayType === -2
            ) {
              <div class="flex-31 layout-row layout-lt-md-column">
                <mat-form-field class="flex-fill">
                  <mat-select formControlName="recalculationCompoundingFrequencyOnDayType">
                    @for (
                      interestRecalculationOnDayType of interestRecalculationOnDayTypeData;
                      track interestRecalculationOnDayType
                    ) {
                      <mat-option [value]="interestRecalculationOnDayType">
                        {{ interestRecalculationOnDayType }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            }
          </div>
        }
        <mat-form-field class="flex-31">
          <mat-label>{{ 'labels.inputs.Frequency for recalculate Outstanding Principal' | translate }}</mat-label>
          <mat-select formControlName="recalculationRestFrequencyType" required>
            @for (
              interestRecalculationFrequencyType of interestRecalculationFrequencyTypeData;
              track interestRecalculationFrequencyType
            ) {
              <mat-option [value]="interestRecalculationFrequencyType.id">
                {{ interestRecalculationFrequencyType.value | translateKey: 'catalogs' }}
              </mat-option>
            }
          </mat-select>
          <mat-error>
            {{ 'labels.inputs.Frequency for recalculate Outstanding Principal' | translate }}
            {{ 'labels.commons.is' | translate }} <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
        @if (loanProductSettingsForm.value.recalculationRestFrequencyType === 4) {
          <div class="flex-31 layout-row layout-lt-md-column">
            <mat-form-field class="flex-fill">
              <mat-select formControlName="recalculationRestFrequencyNthDayType">
                @for (
                  interestRecalculationNthDayType of interestRecalculationNthDayTypeData;
                  track interestRecalculationNthDayType
                ) {
                  <mat-option [value]="interestRecalculationNthDayType.id">
                    {{ interestRecalculationNthDayType.value | translateKey: 'catalogs' }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }
        @if (
          (loanProductSettingsForm.value.recalculationRestFrequencyType === 4 &&
            loanProductSettingsForm.value.recalculationRestFrequencyNthDayType !== -2) ||
          loanProductSettingsForm.value.recalculationRestFrequencyType === 3
        ) {
          <div class="flex-31 layout-row layout-lt-md-column">
            <mat-form-field class="flex-fill">
              <mat-select formControlName="recalculationRestFrequencyDayOfWeekType">
                @for (
                  interestRecalculationDayOfWeekType of interestRecalculationDayOfWeekTypeData;
                  track interestRecalculationDayOfWeekType
                ) {
                  <mat-option [value]="interestRecalculationDayOfWeekType.id">
                    {{ interestRecalculationDayOfWeekType.value | translateKey: 'catalogs' }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }
        @if (
          loanProductSettingsForm.value.recalculationRestFrequencyType === 4 &&
          loanProductSettingsForm.value.recalculationRestFrequencyNthDayType === -2
        ) {
          <div class="flex-31 layout-row layout-lt-md-column">
            <mat-form-field class="flex-fill">
              <mat-select formControlName="recalculationRestFrequencyOnDayType">
                @for (
                  interestRecalculationOnDayType of interestRecalculationOnDayTypeData;
                  track interestRecalculationOnDayType
                ) {
                  <mat-option [value]="interestRecalculationOnDayType">
                    {{ interestRecalculationOnDayType }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }
        <mat-checkbox
          class="flex-98 margin-v"
          labelPosition="before"
          formControlName="isArrearsBasedOnOriginalSchedule"
        >
          {{ 'labels.inputs.Is Arrears recognization based on original schedule' | translate }}?
        </mat-checkbox>
        @if (loanProductSettingsForm.value.loanScheduleType === 'PROGRESSIVE') {
          <mat-checkbox
            class="flex-98 margin-v"
            labelPosition="before"
            formControlName="disallowInterestCalculationOnPastDue"
          >
            {{ 'labels.inputs.Do not calculate interest on past due principal balances' | translate }}
          </mat-checkbox>
        }
      </div>
    }

    <mat-divider class="flex-98"></mat-divider>

    <h3 class="mat-h3 flex-23">{{ 'labels.heading.Guarantee Requirements' | translate }}</h3>

    <mat-checkbox class="flex-73 margin-b" labelPosition="before" formControlName="holdGuaranteeFunds">
      {{ 'labels.inputs.Place Guarantee Funds On-Hold' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.holdGuaranteeFunds) {
      <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <mat-form-field class="flex-31">
          <mat-label>{{ 'labels.inputs.Mandatory Guarantee(%)' | translate }}</mat-label>
          <input type="number" matInput formControlName="mandatoryGuarantee" required />
          <mat-error>
            {{ 'labels.inputs.Mandatory Guarantee' | translate }} {{ 'labels.commons.is' | translate }}
            <strong>{{ 'labels.commons.required' | translate }}</strong>
          </mat-error>
        </mat-form-field>
        <mat-form-field class="flex-31">
          <mat-label>{{ 'labels.inputs.Minimum Guarantee from Own Funds(%)' | translate }}</mat-label>
          <input type="number" matInput formControlName="minimumGuaranteeFromOwnFunds" />
        </mat-form-field>
        <mat-form-field class="flex-31">
          <mat-label>{{ 'labels.inputs.Minimum Guarantee from Guarantor Funds(%)' | translate }}</mat-label>
          <input type="number" matInput formControlName="minimumGuaranteeFromGuarantor" />
        </mat-form-field>
      </div>
    }

    <mat-divider class="flex-98"></mat-divider>

    <h3 class="mat-h3 flex-23">
      {{ 'labels.heading.Event Settings' | translate
      }}<i
        class="fas fa-question"
        matTooltip="{{ 'tooltips.Setting for event notifications to be sent' | translate }}"
      ></i>
    </h3>

    <mat-checkbox
      class="flex-48 margin-b"
      labelPosition="before"
      formControlName="useDueForRepaymentsConfigurations"
      matTooltip="{{ 'tooltips.Use or not the Global Configurations' | translate }}"
    >
      {{ 'labels.inputs.Use the Global Configurations values to the Repayment Event (notifications)' | translate }}
    </mat-checkbox>

    @if (!loanProductSettingsForm.value.useDueForRepaymentsConfigurations) {
      <div class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.Due days for repayment event' | translate }}</mat-label>
          <input
            type="number"
            matInput
            matTooltip="{{ 'tooltips.Maximum outstanding loan account balance' | translate }}"
            formControlName="dueDaysForRepaymentEvent"
          />
        </mat-form-field>
        <mat-form-field class="flex-48">
          <mat-label>{{ 'labels.inputs.OverDue days for repayment event' | translate }}</mat-label>
          <input
            type="number"
            matInput
            matTooltip="{{ 'tooltips.Maximum outstanding loan account balance' | translate }}"
            formControlName="overDueDaysForRepaymentEvent"
          />
        </mat-form-field>
      </div>
    }

    <mat-divider class="flex-98"></mat-divider>

    <h3 class="mat-h3 flex-43">{{ 'labels.inputs.Configurable Terms and Settings' | translate }}</h3>

    <mat-checkbox labelPosition="before" formControlName="allowAttributeConfiguration" class="margin-b flex-53">
      {{ 'labels.inputs.Allow overriding select terms and settings in loan accounts' | translate }}
    </mat-checkbox>

    @if (loanProductSettingsForm.value.allowAttributeConfiguration) {
      <div formGroupName="allowAttributeOverrides" class="flex-fill layout-row-wrap gap-2percent responsive-column">
        <mat-checkbox labelPosition="before" formControlName="amortizationType" class="margin-v flex-48">
          {{ 'labels.inputs.Amortization' | translate }}
        </mat-checkbox>
        <mat-checkbox labelPosition="before" formControlName="interestType" class="margin-v flex-48">
          {{ 'labels.inputs.Interest method' | translate }}
        </mat-checkbox>
        <mat-checkbox
          class="flex-48 margin-v"
          labelPosition="before"
          formControlName="transactionProcessingStrategyCode"
        >
          {{ 'labels.inputs.Repayment strategy' | translate }}
        </mat-checkbox>
        <mat-checkbox class="flex-48 margin-v" labelPosition="before" formControlName="interestCalculationPeriodType">
          {{ 'labels.inputs.Interest calculation period' | translate }}
        </mat-checkbox>
        <mat-checkbox labelPosition="before" formControlName="inArrearsTolerance" class="margin-v flex-48">
          {{ 'labels.inputs.Arrears tolerance' | translate }}
        </mat-checkbox>
        <mat-checkbox labelPosition="before" formControlName="repaymentEvery" class="margin-v flex-48">
          {{ 'labels.inputs.Repaid every' | translate }}
        </mat-checkbox>
        <mat-checkbox
          class="flex-48 margin-v"
          labelPosition="before"
          formControlName="graceOnPrincipalAndInterestPayment"
        >
          {{ 'labels.inputs.Moratorium' | translate }}
        </mat-checkbox>
        <mat-checkbox labelPosition="before" formControlName="graceOnArrearsAgeing" class="margin-v flex-48">
          {{ 'labels.inputs.Number of days a loan may be overdue before moving into arrears' | translate }}
        </mat-checkbox>
      </div>
    }
  </div>

  <div class="layout-row align-center gap-2percent margin-t responsive-column">
    <button mat-raised-button matStepperPrevious>
      <fa-icon icon="arrow-left" class="m-r-10"></fa-icon>
      {{ 'labels.buttons.Previous' | translate }}
    </button>
    <button mat-raised-button matStepperNext>
      {{ 'labels.buttons.Next' | translate }}
      <fa-icon icon="arrow-right" class="m-l-10"></fa-icon>
    </button>
  </div>
</form>
